<template>
  <div class="fixed bottom-4 right-4 bg-gray-900 text-white p-4 rounded-lg shadow-2xl z-50 max-w-md">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-sm font-bold text-yellow-400">🐛 Debug Panel - Audit Logs</h3>
      <button @click="toggle" class="text-gray-400 hover:text-white">
        <i class="fas" :class="isOpen ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
      </button>
    </div>

    <div v-if="isOpen" class="space-y-3">
      <!-- Status -->
      <div class="text-xs space-y-1">
        <div class="flex items-center gap-2">
          <span class="text-gray-400">Status:</span>
          <span :class="loading ? 'text-yellow-400' : 'text-green-400'">
            {{ loading ? 'Loading...' : 'Ready' }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-gray-400">User:</span>
          <span class="text-blue-400">{{ authStore.user?.name || 'Not logged in' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-gray-400">User ID:</span>
          <span class="text-blue-400">{{ authStore.user?.id || 'N/A' }}</span>
        </div>
      </div>

      <!-- Test Buttons -->
      <div class="flex gap-2">
        <button
          @click="testRecentEndpoint"
          :disabled="loading"
          class="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded disabled:opacity-50"
        >
          <i class="fas fa-flask mr-1"></i>
          Test Recent API
        </button>
        <button
          @click="testUserEndpoint"
          :disabled="loading"
          class="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded disabled:opacity-50"
        >
          <i class="fas fa-user mr-1"></i>
          Test User API
        </button>
      </div>

      <!-- Results -->
      <div v-if="result" class="mt-3">
        <div class="text-xs text-gray-400 mb-1">Last Result:</div>
        <div class="bg-gray-800 rounded p-2 text-xs overflow-auto max-h-60">
          <div v-if="result.success" class="text-green-400 mb-2">✅ Success</div>
          <div v-else class="text-red-400 mb-2">❌ Error</div>

          <div class="space-y-2">
            <div>
              <div class="text-yellow-400">Endpoint:</div>
              <div class="text-gray-300">{{ result.endpoint }}</div>
            </div>

            <div v-if="result.response">
              <div class="text-yellow-400">Response Type:</div>
              <div class="text-gray-300">{{ result.responseType }}</div>
            </div>

            <div v-if="result.count !== null">
              <div class="text-yellow-400">Items Count:</div>
              <div class="text-gray-300">{{ result.count }}</div>
            </div>

            <div v-if="result.error">
              <div class="text-yellow-400">Error:</div>
              <div class="text-red-300">{{ result.error }}</div>
            </div>

            <div v-if="result.sample" class="mt-2">
              <div class="text-yellow-400">Sample Item:</div>
              <pre class="text-gray-300 text-xs overflow-auto">{{ JSON.stringify(result.sample, null, 2) }}</pre>
            </div>

            <div v-if="result.raw" class="mt-2">
              <div class="text-yellow-400">Raw Response:</div>
              <pre class="text-gray-300 text-xs overflow-auto max-h-40">{{ JSON.stringify(result.raw, null, 2) }}</pre>
            </div>
          </div>
        </div>
      </div>

      <!-- Instructions -->
      <div class="text-xs text-gray-400 border-t border-gray-700 pt-2">
        <div class="font-semibold text-yellow-400 mb-1">Testing Steps:</div>
        <ol class="list-decimal list-inside space-y-1">
          <li>Open browser console (F12)</li>
          <li>Click "Test Recent API" button</li>
          <li>Check the response structure</li>
          <li>Try login/logout and test again</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'

const authStore = useAuthStore()
const isOpen = ref(true)
const loading = ref(false)
const result = ref(null)

function toggle() {
  isOpen.value = !isOpen.value
}

async function testRecentEndpoint() {
  loading.value = true
  result.value = null

  try {
    console.log('🧪 Testing recent activities endpoint...')
    const response = await api.get('/audit-logs/recent', { params: { limit: 20 } })

    console.log('📦 Raw response:', response)
    console.log('📦 Response type:', typeof response)
    console.log('📦 Is array:', Array.isArray(response))
    console.log('📦 Keys:', response ? Object.keys(response) : 'null')

    let activities = []
    let count = 0
    let sample = null

    // Try different structures
    if (Array.isArray(response?.data)) {
      activities = response.data
      console.log('✅ Found in response.data')
    } else if (Array.isArray(response)) {
      activities = response
      console.log('✅ Found as direct array')
    } else if (response?.activities && Array.isArray(response.activities)) {
      activities = response.activities
      console.log('✅ Found in response.activities')
    }

    count = activities.length
    if (count > 0) {
      sample = activities[0]
      console.log('🎯 Sample:', sample)
      console.log('🔑 All IDs:', activities.map(a => a.id).join(', '))
    }

    result.value = {
      success: true,
      endpoint: '/audit-logs/recent',
      responseType: typeof response,
      isArray: Array.isArray(response),
      keys: response ? Object.keys(response) : [],
      count,
      sample,
      raw: response
    }

    console.log('✅ Test completed:', result.value)
  } catch (error) {
    console.error('❌ Test failed:', error)
    result.value = {
      success: false,
      endpoint: '/audit-logs/recent',
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText
    }
  } finally {
    loading.value = false
  }
}

async function testUserEndpoint() {
  loading.value = true
  result.value = null

  if (!authStore.user?.id) {
    result.value = {
      success: false,
      endpoint: '/audit-logs/user/:id/activity',
      error: 'User not logged in'
    }
    loading.value = false
    return
  }

  try {
    console.log('🧪 Testing user activities endpoint...')
    const userId = authStore.user.id
    const response = await api.get(`/audit-logs/user/${userId}/activity`, {
      params: { per_page: 20 }
    })

    console.log('📦 Raw response:', response)
    console.log('📦 Response type:', typeof response)
    console.log('📦 Is array:', Array.isArray(response))
    console.log('📦 Keys:', response ? Object.keys(response) : 'null')

    let activities = []
    let count = 0
    let sample = null

    // Try different structures
    if (Array.isArray(response?.data)) {
      activities = response.data
      console.log('✅ Found in response.data')
    } else if (Array.isArray(response)) {
      activities = response
      console.log('✅ Found as direct array')
    } else if (response?.activities && Array.isArray(response.activities)) {
      activities = response.activities
      console.log('✅ Found in response.activities')
    }

    count = activities.length
    if (count > 0) {
      sample = activities[0]
      console.log('🎯 Sample:', sample)
      console.log('🔑 All IDs:', activities.map(a => a.id).join(', '))
    }

    result.value = {
      success: true,
      endpoint: `/audit-logs/user/${userId}/activity`,
      responseType: typeof response,
      isArray: Array.isArray(response),
      keys: response ? Object.keys(response) : [],
      count,
      sample,
      raw: response
    }

    console.log('✅ Test completed:', result.value)
  } catch (error) {
    console.error('❌ Test failed:', error)
    result.value = {
      success: false,
      endpoint: `/audit-logs/user/${authStore.user.id}/activity`,
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
